import { useState } from 'react'
import { useMembers } from '../../../hooks/useMembers'
import { useAuth } from '../../../hooks/useAuth'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const FREQUENCIES = ['once_daily','twice_daily','three_times_daily','weekly','as_needed','other']

export const MedicationForm = ({ medication, onSave, onClose }) => {
  const { user, isAdmin } = useAuth()
  const { data: members = [] } = useMembers()
  const realMembers = members.filter((m) => !m.isShared)

  const [memberId,        setMemberId]        = useState(medication?.member_id       ?? user?.id ?? '')
  const [name,            setName]            = useState(medication?.name            ?? '')
  const [dosage,          setDosage]          = useState(medication?.dosage          ?? '')
  const [frequency,       setFrequency]       = useState(medication?.frequency       ?? 'once_daily')
  const [freqNotes,       setFreqNotes]       = useState(medication?.frequency_notes ?? '')
  const [prescriber,      setPrescriber]      = useState(medication?.prescriber      ?? '')
  const [pharmacy,        setPharmacy]        = useState(medication?.pharmacy        ?? '')
  const [rxNumber,        setRxNumber]        = useState(medication?.rx_number       ?? '')
  const [startDate,       setStartDate]       = useState(medication?.start_date      ?? '')
  const [endDate,         setEndDate]         = useState(medication?.end_date        ?? '')
  const [refillDate,      setRefillDate]      = useState(medication?.refill_date     ?? '')
  const [notes,           setNotes]           = useState(medication?.notes           ?? '')
  const [isActive,        setIsActive]        = useState(medication?.is_active       ?? true)
  const [saving,          setSaving]          = useState(false)
  const [error,           setError]           = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim())   { setError('Medication name is required.'); return }
    if (!dosage.trim()) { setError('Dosage is required.'); return }
    if (!memberId)      { setError('Select a member.'); return }
    setSaving(true)
    setError('')
    try {
      await onSave({
        member_id:       memberId,
        name:            name.trim(),
        dosage:          dosage.trim(),
        frequency,
        frequency_notes: freqNotes.trim()  || null,
        prescriber:      prescriber.trim() || null,
        pharmacy:        pharmacy.trim()   || null,
        rx_number:       rxNumber.trim()   || null,
        start_date:      startDate         || null,
        end_date:        endDate           || null,
        refill_date:     refillDate        || null,
        notes:           notes.trim()      || null,
        is_active:       isActive,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">
            {medication ? 'Edit Medication' : 'Add Medication'}
          </h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          {/* Member selector — admins only see all, others only themselves */}
          {isAdmin ? (
            <div>
              {lbl('For Member *')}
              <select value={memberId} onChange={(e) => setMemberId(e.target.value)}
                className={inputCls + ' bg-surface'}>
                <option value="">Select member…</option>
                {realMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.profiles?.display_name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="text-sm text-text-muted bg-background rounded-xl px-3 py-2">
              Medication for: <strong>{realMembers.find((m) => m.user_id === user?.id)?.profiles?.display_name ?? 'You'}</strong>
            </div>
          )}

          {/* Name + Dosage */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              {lbl('Medication Name *')}
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Lisinopril" className={inputCls} />
            </div>
            <div>
              {lbl('Dosage *')}
              <input type="text" value={dosage} onChange={(e) => setDosage(e.target.value)}
                placeholder="10mg" className={inputCls} />
            </div>
          </div>

          {/* Frequency */}
          <div>
            {lbl('Frequency')}
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
              className={inputCls + ' bg-surface'}>
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            {lbl('Frequency Notes')}
            <input type="text" value={freqNotes} onChange={(e) => setFreqNotes(e.target.value)}
              placeholder="e.g. with food, at bedtime" className={inputCls} />
          </div>

          {/* Prescriber + Pharmacy */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              {lbl('Prescriber')}
              <input type="text" value={prescriber} onChange={(e) => setPrescriber(e.target.value)}
                placeholder="Dr. Smith" className={inputCls} />
            </div>
            <div>
              {lbl('Pharmacy')}
              <input type="text" value={pharmacy} onChange={(e) => setPharmacy(e.target.value)}
                placeholder="CVS Pharmacy" className={inputCls} />
            </div>
          </div>

          <div>
            {lbl('Rx Number')}
            <input type="text" value={rxNumber} onChange={(e) => setRxNumber(e.target.value)}
              placeholder="RX1234567" className={inputCls} />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              {lbl('Start Date')}
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              {lbl('End Date')}
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              {lbl('Refill Date')}
              <input type="date" value={refillDate} onChange={(e) => setRefillDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Notes + Active toggle */}
          <div>
            {lbl('Notes')}
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} placeholder="Any additional notes…"
              className={inputCls + ' resize-none'} />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <button type="button" onClick={() => setIsActive((v) => !v)}
              className={cn(
                'relative w-10 h-6 rounded-full transition-colors flex-shrink-0',
                isActive ? 'bg-primary' : 'bg-border',
              )}>
              <span className={cn(
                'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all',
                isActive ? 'left-4.5' : 'left-0.5',
              )} />
            </button>
            <span className="text-sm font-medium text-text-primary">Active</span>
          </label>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">Cancel</button>
          <button type="submit" disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save Medication'}
          </button>
        </div>
      </form>
    </div>
    </Portal>
  )
}
